import {
  JobListResponse,
  SavedSearchResponse,
} from '@doda/api-client/aspida/@types';
import {
  AppQueryParams,
  QueryOtherParams,
} from '@doda/common/src/constants/queryParams';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { createSearchConditionRequestParams } from 'logics/createSearchConditionRequestParams';
import { IncomingHttpHeaders } from 'http';
import { Methods as JobSearchListRequestType } from '@doda/api-client/aspida/api/v1/jobs';
import { convertSaveSearchesToJobSearches } from 'logics/savedSearches';
import {
  isErrorResponseBody,
  nonNullable,
} from '@doda/common/src/utils/typeGuard';
import { createNearAreaJobListRequestParams } from '@doda/common/src/logics/createNearAreaJobListRequestParams';
import { getNearAreaJobList } from '@doda/common/src/services/getNearAreaJobList';
import { getJobSearchList } from '@doda/common/src/services/getJobSearchList';
import { setValidationMessages } from '..';

/**
 * RejectedValue
 */
export interface RejectedValue {
  message: string;
  redirect: boolean;
}

// TODO: src/constants/jobList で定義してる JOB_COUNT_PER_PAGE に差し替える
export const DISPLAY_NUMBER = 50;

/**
 * Fetches the job list based on provided query parameters.
 *
 * @param {Object} params - Parameters for fetching job list.
 * @param {IncomingHttpHeaders} params.headers - Headers for the request.
 * @param {AppQueryParams} params.query - Query parameters for fetching job list.
 * @param {QueryOtherParams} params.queryother - Additional query parameters.
 * @param {boolean} params.useV2JobSearchListApi - V2 job search list api flag.
 * @returns {Promise<JobListResponse>} - Job list response.
 */
export const fetchJobList = createAsyncThunk<
  JobListResponse,
  {
    headers?: IncomingHttpHeaders;
    query: AppQueryParams;
    queryother?: QueryOtherParams;
    useV2JobSearchListApi: boolean;
    isNearAreaV2?: boolean;
  },
  { rejectValue: RejectedValue }
>(
  'jobList/fetchJobList',
  async (
    { headers, query, queryother, useV2JobSearchListApi, isNearAreaV2 },
    thunkAPI
  ) => {
    try {
      if (query.nearArea) {
        const param = createNearAreaJobListRequestParams(query);
        return await getNearAreaJobList(
          { ...param, limit: DISPLAY_NUMBER },
          {
            isNearAreaV2,
            limitDefault: DISPLAY_NUMBER,
          },
          headers
        );
      }
      const param = createSearchConditionRequestParams(query, queryother);
      return await getJobSearchList({
        query: param,
        headers,
        useV2JobSearchListApi,
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      // バリデーションチェックに失敗した場合
      if (axiosError.response?.status === 400) {
        if (isErrorResponseBody(axiosError.response?.data)) {
          const validationMessages = axiosError.response.data.details
            .map(({ message }) => message)
            .filter(nonNullable);
          thunkAPI.dispatch(setValidationMessages(validationMessages));
        }
        return thunkAPI.rejectWithValue({
          message: `jobList/fetchJobList status 400, api request path: ${decodeURI(
            axiosError.request?.path
          )}`,
          redirect: false,
        });
      }
      return thunkAPI.rejectWithValue({ message: '', redirect: false });
    }
  }
);

/**
 * Fetches the job list based on saved searches.
 *
 * @param {Object} params - Parameters for fetching saved searches job list.
 * @param {IncomingHttpHeaders} params.headers - Headers for the request.
 * @param {Omit<SavedSearchResponse, 'no' | 'name' | 'savedDate'>} params.savedSearches - Saved searches data.
 * @param {JobSearchListRequestType['get']['query']} [params.optionParam] - Optional additional query parameters.
 * @param {boolean} [params.useV2JobSearchListApi] - V2 job search list flag.
 * @returns {Promise<JobListResponse>} - Job list response.
 */
export const fetchSaveSearchesJobList = createAsyncThunk<
  JobListResponse,
  {
    headers?: IncomingHttpHeaders;
    savedSearches: Omit<SavedSearchResponse, 'no' | 'name' | 'savedDate'>;
    optionParam?: JobSearchListRequestType['get']['query'];
    useV2JobSearchListApi: boolean;
  },
  {
    rejectValue: RejectedValue;
  }
>(
  'jobList/fetchSaveSearchesJobList',
  async (
    { headers, savedSearches, optionParam, useV2JobSearchListApi },
    thunkAPI
  ) => {
    try {
      const params = convertSaveSearchesToJobSearches(savedSearches);
      return await getJobSearchList({
        query: {
          ...params,
          limit: DISPLAY_NUMBER,
          ...optionParam,
        },
        headers,
        useV2JobSearchListApi,
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      // バリデーションチェックに失敗した場合
      if (axiosError.response?.status === 400) {
        if (isErrorResponseBody(axiosError.response?.data)) {
          const validationMessages = axiosError.response.data.details
            .map(({ message }) => message)
            .filter(nonNullable);
          thunkAPI.dispatch(setValidationMessages(validationMessages));
        }
        return thunkAPI.rejectWithValue({
          message: `jobList/fetchSaveSearchesJobList status 400, api request path: ${decodeURI(
            axiosError.request?.path
          )}`,
          redirect: false,
        });
      }
      return thunkAPI.rejectWithValue({ message: '', redirect: false });
    }
  }
);
